import {
  ActorRole,
  EditPricingModeDocument,
  PricingMode,
  ReleaseDealControlDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { editionModeState } from '@/states/edition-mode.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';

/**
 * Rend la main à la marque (Choose → Brand).
 *
 * Règle métier : une marque ne renseigne que son prix de rétrocession, jamais
 * la marge Choose. Le mode `MARGIN_FIXED` rendrait la marge éditable et le prix
 * de rétrocession en lecture seule pour la marque. On force donc le deal en
 * `BUYING_PRICE_FIXED` **avant** de rendre la main (tant que Choose a encore
 * le contrôle, la mutation `updateDealPricingMode` est autorisée).
 */
export function useReleaseDealControl(dealId: string) {
  const setEditionMode = useSetAtom(editionModeState);
  const pricingMode = useAtomValue(pricingModeState);
  const setPricingMode = useSetAtom(pricingModeState);
  const [releaseDealControlMutation, { loading, error }] = useMutation(
    ReleaseDealControlDocument
  );
  const [editPricingModeMutation, { loading: pricingModeLoading }] =
    useMutation(EditPricingModeDocument);

  const ensureBuyingPriceFixed = async (): Promise<boolean> => {
    if (pricingMode === PricingMode.BuyingPriceFixed) {
      return false;
    }

    const result = await editPricingModeMutation({
      variables: { dealId, pricingMode: PricingMode.BuyingPriceFixed },
    });

    const payload = result.data?.updateDealPricingMode;
    if (payload?.__typename === 'DomainException') {
      throw new Error(payload.code);
    }

    setPricingMode(PricingMode.BuyingPriceFixed);
    trackEvent('PRICING_MODE_CHANGED', {
      dealId,
      modeTo: PricingMode.BuyingPriceFixed,
      source: 'release_control',
    });
    return true;
  };

  const releaseDealControl = async () => {
    const pricingModeSwitched = await ensureBuyingPriceFixed();

    const result = await releaseDealControlMutation({
      variables: { dealId },
    });

    const response = result.data?.releaseDealControl;
    if (response?.__typename === 'DomainException') {
      throw new Error(response.code);
    }

    setEditionMode(ActorRole.Brand);
    trackEvent('DEAL_CONTROL_RELEASED', { dealId, pricingModeSwitched });
    return result;
  };

  return {
    releaseDealControl,
    loading: loading || pricingModeLoading,
    error,
  };
}
